import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { MapPin, Briefcase, IndianRupee, BookOpen, Clock, Mail, Phone, Unlock, Coins, ArrowLeft, Building2, Shield } from 'lucide-react';
import ShareButton from '../../components/ShareButton';
import apiClient from '../../services/apiClient';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const mapContainerStyle = { width: '100%', height: '400px', borderRadius: '8px' };
const libraries = ['places'];

export const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [contactUnlocked, setContactUnlocked] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);
  const [showMap, setShowMap] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    if (token) {
      fetchWallet();
      checkPurchaseStatus();
    }
  }, [jobId, token]);

  const fetchJobDetails = async () => {
    try {
      const data = await apiClient.get(`/jobs/${jobId}`);
      setJob(data);
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const fetchWallet = async () => {
    try {
      const data = await apiClient.get('/wallet/balance');
      setWallet({ balance: data.balance || 0 });
    } catch (error) {
      console.error('Failed to fetch wallet');
    }
  };

  const checkPurchaseStatus = async () => {
    try {
      const data = await apiClient.get(
        `/check-purchase-status?target_type=job&target_id=${jobId}`
      );
      if (data.purchased && !data.expired) {
        setContactUnlocked(true);
        setContactDetails(data.contact_details);
      }
    } catch (error) {
      console.error('Failed to check purchase status');
    }
  };

  const handleUnlock = async () => {
    if (!token) {
      toast.error('Please login to unlock contact');
      navigate('/login');
      return;
    }

    if (wallet.balance < job.unlock_price) {
      toast.error(`Insufficient balance. Need ${job.unlock_price} coins`);
      return;
    }

    setUnlocking(true);
    try {
      const data = await apiClient.post(
        `/unlock-contact?target_type=job&target_id=${jobId}`
      );

      toast.success(`Contact unlocked! ${data.coins_deducted} coins deducted`);
      setContactUnlocked(true);
      setContactDetails(data.contact_details);
      fetchWallet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock contact');
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center"><div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div><p className="mt-4 text-gray-600">Loading...</p></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center"><p className="text-xl text-gray-600">Job not found</p><Button onClick={() => navigate('/find-job')} className="mt-4">Browse Jobs</Button></div>
      </div>
    );
  }

  const mapCenter = job.latitude && job.longitude ? { lat: job.latitude, lng: job.longitude } : { lat: 28.6139, lng: 77.2090 };
  const needsApproval = job.review_status === 'pending';
  const isApproved = job.review_status === 'approved';

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 lg:px-8">
        <Button variant="outline" onClick={() => navigate('/find-job')} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Jobs
        </Button>

        {needsApproval && (
          <Alert className="mb-6 bg-yellow-50 border-yellow-200">
            <Clock className="h-4 w-4" />
            <AlertDescription>This job is under review. It will be visible to others once approved and pricing is set.</AlertDescription>
          </Alert>
        )}

        <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Main Details */}
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 flex-shrink-0" />
                      <CardTitle className="text-lg sm:text-2xl">{job.title}</CardTitle>
                    </div>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <Badge variant="default">{job.type}</Badge>
                      {isApproved && <Badge variant="outline" className="bg-green-50 text-green-700">Approved</Badge>}
                      {needsApproval && <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Pending Review</Badge>}
                      {job.posted_by_admin && <Badge className="bg-gradient-to-r from-red-500 to-orange-500 text-white border-0"><Shield className="h-3 w-3 mr-1" />Admin</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ShareButton
                      title={job.title}
                      text={`Check out this job opportunity: ${job.title} at ${job.organization || 'Avyudha'}`}
                      variant="outline"
                      size="sm"
                    />
                    {isApproved && !contactUnlocked && job.unlock_price > 0 && (
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Unlock Price</div>
                        <div className="flex items-center text-2xl font-bold text-blue-600">
                          <Coins className="h-5 w-5 mr-1" />
                          {job.unlock_price}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 space-y-4">
                {job.description && (
                  <div>
                    <h3 className="font-semibold text-gray-700 mb-2">Description</h3>
                    <p className="text-gray-600 text-sm sm:text-base">{job.description}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Organization</p>
                      {contactUnlocked ? (
                        <p className="font-medium">{job.organization}</p>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-400">••••••••</p>
                          <Badge variant="outline" className="text-xs">
                            <Unlock className="h-3 w-3 mr-1" />
                            Locked
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <IndianRupee className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Salary</p>
                      <p className="font-medium">{job.salary}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Experience Required</p>
                      <p className="font-medium">{job.experience}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-medium">{job.location}</p>
                    </div>
                  </div>
                </div>

                {job.subjects && job.subjects.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <BookOpen className="h-5 w-5 text-blue-600" />
                      <h3 className="font-semibold text-gray-700">Subjects</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {job.subjects.map((subject, idx) => (
                        <Badge key={idx} variant="secondary">{subject}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location Map */}
            {job.latitude && job.longitude && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center"><MapPin className="h-5 w-5 mr-2" />Job Location</CardTitle>
                    {!contactUnlocked && (
                      <Badge variant="outline" className="text-xs">
                        <Unlock className="h-3 w-3 mr-1" />
                        Locked - Purchase to view
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!showMap ? (
                    <div className="text-center py-8">
                      <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500 mb-4">Click below to view job location on map</p>
                      <Button
                        variant="outline"
                        onClick={() => setShowMap(true)}
                        disabled={!contactUnlocked}
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        View Location on Map
                      </Button>
                      {!contactUnlocked && (
                        <p className="text-xs text-gray-400 mt-2">Unlock contact details first</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                        <GoogleMap mapContainerStyle={mapContainerStyle} center={mapCenter} zoom={13}>
                          <Marker position={mapCenter} />
                        </GoogleMap>
                      </LoadScript>
                      <p className="mt-4 text-gray-700">{job.location}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card>
              <CardHeader>
                <CardTitle>Organization Contact</CardTitle>
              </CardHeader>
              <CardContent>
                {contactUnlocked ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-green-600">
                      <Unlock className="h-5 w-5" />
                      <span className="font-semibold">Contact Unlocked</span>
                    </div>
                    {contactDetails?.organization && (
                      <div className="flex items-start space-x-2">
                        <Building2 className="h-4 w-4 mt-1 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Organization</p>
                          <p className="font-medium">{contactDetails.organization}</p>
                        </div>
                      </div>
                    )}
                    {contactDetails?.email && (
                      <div className="flex items-start space-x-2">
                        <Mail className="h-4 w-4 mt-1 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Email</p>
                          <p className="font-medium">{contactDetails.email}</p>
                        </div>
                      </div>
                    )}
                    {contactDetails?.phone && (
                      <div className="flex items-start space-x-2">
                        <Phone className="h-4 w-4 mt-1 text-gray-600" />
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="font-medium">{contactDetails.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {!isApproved ? (
                      <Alert>
                        <AlertDescription>Contact will be available once admin approves this job.</AlertDescription>
                      </Alert>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">Unlock organization contact details to apply for this job.</p>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">Your Balance</span>
                            <span className="font-bold text-lg">{wallet.balance} coins</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Unlock Price</span>
                            <span className="font-bold text-lg text-blue-600">{job.unlock_price} coins</span>
                          </div>
                        </div>
                        <Button
                          onClick={handleUnlock}
                          disabled={unlocking || wallet.balance < job.unlock_price}
                          className="w-full"
                        >
                          {unlocking ? 'Unlocking...' : `Unlock Contact (${job.unlock_price} coins)`}
                        </Button>
                        {wallet.balance < job.unlock_price && (
                          <p className="text-xs text-red-600 text-center">Insufficient balance. Please add coins to your wallet.</p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Job Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-sm text-gray-600 space-y-2">
                  <p><span className="font-medium">Posted:</span> {job.posted_date}</p>
                  <p><span className="font-medium">Job Type:</span> {job.type}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};